import * as React from 'react';
import { LandingPageState } from './types';
import { PropsWithChildrenOnly } from '@ids/types/utility';
import { useFetchedUniversalState, useUniversalFetch, useUniversalFetchingHelpers } from '@ids/appshell/hooks';
import { FetchedUniversalState, showOopsPage } from '@ids/appshell';

const LandingPageContext = React.createContext<LandingPageState>(null);
LandingPageContext.displayName = 'LandingPageContext';

const LandingPageProvider = ({ children }: PropsWithChildrenOnly) => {
    const { get } = useUniversalFetchingHelpers();
    const data = useFetchedUniversalState('fetchLandingPage');

    useUniversalFetch(
        'fetchLandingPage',
        async () => {
            if (!data) {
                return get<FetchedUniversalState['fetchLandingPage']>({ path: '/web/v1/ids/landing' });
            }
            return undefined;
        },
        [data]
    );

    const idsLandingPageState: LandingPageState = React.useMemo(() => data?.data ?? null, [data]);

    if ((data?.errors || [])?.length > 0) {
        showOopsPage();
    }

    return <LandingPageContext.Provider value={idsLandingPageState}>{children}</LandingPageContext.Provider>;
};

const useLandingPageContext = () => React.useContext(LandingPageContext);

const useIDSSku = () => useLandingPageContext()?.sku;
const useHeroBlock = () => useLandingPageContext()?.heroBlock;
const useOurProjectsBlock = () => useLandingPageContext()?.ourProjectsBlock;
const useProjectSliderBlock = () => useLandingPageContext()?.projectSliderBlock;
const usePriceTiersBlock = () => useLandingPageContext()?.priceTiersBlock;
const useOfflineStudioBlock = () => useLandingPageContext()?.offlineStudioBlock;
const useWhatYouGetBlock = () => useLandingPageContext()?.whatYouGetBlock;
const useHowItWorksBlock = () => useLandingPageContext()?.howItWorksBlock;
const useReviewsBlock = () => useLandingPageContext()?.reviewsBlock;
const useFAQBlock = () => useLandingPageContext()?.faqBlock;
const usePressBlock = () => useLandingPageContext()?.pressBlock;

export {
    LandingPageProvider,
    useLandingPageContext,
    useIDSSku,
    useHeroBlock,
    useOurProjectsBlock,
    useProjectSliderBlock,
    useHowItWorksBlock,
    usePriceTiersBlock,
    useWhatYouGetBlock,
    useReviewsBlock,
    useFAQBlock,
    usePressBlock,
    useOfflineStudioBlock,
};
